import {
  BLOCK_TYPES,
  DEFAULT_BLOCKS,
  newPageBuilderBlockId,
} from "./pageBuilderSchema";

const VALID_TYPES = new Set(Object.values(BLOCK_TYPES));

/**
 * Strip optional ```json ... ``` wrapper from pasted text.
 */
export function stripJsonCodeFence(text) {
  let s = String(text || "").trim();
  if (!s) return s;
  const whole = /^```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```\s*$/i;
  const mWhole = s.match(whole);
  if (mWhole) return mWhole[1].trim();
  const inner = /```(?:json)?\s*\r?\n?([\s\S]*?)\r?\n?```/i;
  const mInner = s.match(inner);
  if (mInner) return mInner[1].trim();
  return s;
}

function mergeStyles(defStyles, rawStyles) {
  if (!defStyles && !rawStyles) return undefined;
  if (!defStyles) return { ...rawStyles };
  if (!rawStyles) return { ...defStyles };
  return { ...defStyles, ...rawStyles };
}

/**
 * Normalize one block from AI/JSON: merge defaults, assign new id, recurse children.
 */
export function normalizeImportedBlock(raw) {
  if (!raw || typeof raw !== "object") return null;
  const type = raw.type;
  if (!VALID_TYPES.has(type)) return null;

  const def = DEFAULT_BLOCKS[type];
  const id = newPageBuilderBlockId();

  if (type === BLOCK_TYPES.SECTION) {
    const kids = Array.isArray(raw.children) ? raw.children : [];
    return {
      ...def,
      ...raw,
      id,
      type,
      backgroundColor: raw.backgroundColor ?? def.backgroundColor,
      padding: raw.padding ?? def.padding,
      borderRadius: raw.borderRadius ?? def.borderRadius,
      margin: raw.margin ?? def.margin,
      children: kids.map((c) => normalizeImportedBlock(c)).filter(Boolean),
    };
  }

  if (type === BLOCK_TYPES.COLUMNS) {
    const n = Math.min(6, Math.max(1, Number(raw.columns) || def.columns));
    const gap = raw.gap != null ? raw.gap : def.gap;
    let cols = raw.children;
    if (!Array.isArray(cols)) {
      cols = Array.from({ length: n }, () => []);
    } else {
      cols = cols.slice(0, n).map((col) =>
        (Array.isArray(col) ? col : []).map((c) => normalizeImportedBlock(c)).filter(Boolean)
      );
      while (cols.length < n) cols.push([]);
    }
    return {
      ...def,
      ...raw,
      id,
      type,
      columns: n,
      gap,
      children: cols,
    };
  }

  const merged = {
    ...def,
    ...raw,
    id,
    type,
  };

  if (def.styles) {
    merged.styles = mergeStyles(def.styles, raw.styles);
  }

  return merged;
}

/**
 * Parse pasted string into an array of root-level blocks.
 * Accepts: raw array, { blocks }, or { content: { blocks } } (export shape).
 */
export function parsePastedBlocksJson(text) {
  const trimmed = stripJsonCodeFence(text);
  if (!trimmed) {
    const err = new Error("empty");
    err.code = "empty";
    throw err;
  }

  let data;
  try {
    data = JSON.parse(trimmed);
  } catch {
    const err = new Error("invalid_json");
    err.code = "invalid_json";
    throw err;
  }

  let arr;
  if (Array.isArray(data)) arr = data;
  else if (data && Array.isArray(data.blocks)) arr = data.blocks;
  else if (data && data.content && Array.isArray(data.content.blocks)) arr = data.content.blocks;
  else {
    const err = new Error("no_blocks_array");
    err.code = "no_blocks_array";
    throw err;
  }

  const normalized = arr.map((b) => normalizeImportedBlock(b)).filter(Boolean);
  if (normalized.length === 0) {
    const err = new Error("no_valid_blocks");
    err.code = "no_valid_blocks";
    throw err;
  }
  return normalized;
}

/** Minimal example for "insert sample" in the UI */
export const SAMPLE_BLOCKS_JSON = `{
  "blocks": [
    {
      "type": "header",
      "level": 1,
      "content": "Velkommen",
      "styles": { "fontSize": "36px", "color": "#0f172a", "padding": "16px" }
    },
    {
      "type": "text",
      "content": "<p>Dette er et <strong>avsnitt</strong> med HTML.</p>",
      "styles": { "fontSize": "16px", "color": "#334155", "padding": "0 16px" }
    },
    {
      "type": "button",
      "text": "Les mer",
      "link": "https://example.com",
      "styles": {
        "backgroundColor": "#0ea5e9",
        "color": "#ffffff",
        "padding": "12px 24px",
        "borderRadius": "8px",
        "textDecoration": "none",
        "display": "inline-block"
      }
    }
  ]
}`;
